import React, { Fragment, useEffect, useState, useContext } from 'react'
import { Context } from '../context';
import { TableProjectsInfo } from './TableProjectsInfo';
import { DownCircleOutlined, InfoCircleOutlined, InfoOutlined, UpCircleOutlined } from '@ant-design/icons';
import { Button, Popover, Tag, Spin, Tooltip, Checkbox } from 'antd';
import { ProjectMaterials } from './ProjectMaterials';
import { Part } from './Part';
import { BulkActionsPopover } from './BulkActionsPopover';
import { getAllLanguages, getLanguageFlag, Flag, getLanguageProgressForProject, getStageColor, getLanguageTaskStatus, normalizeLangId } from '../utils';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LaunchIcon from '@mui/icons-material/Launch';

// Компонент пометки этапа языка
const LanguageProgressBadge = ({ progress }) => {
  if (!progress) return null;

  const bgColor = progress.isComplete ? '#52c41a' : getStageColor(progress.stageIndex);

  return (
    <Tooltip title={progress.label}>
      <div style={{
        fontSize: '8px',
        fontWeight: 600,
        padding: '1px 3px',
        borderRadius: '3px',
        backgroundColor: bgColor,
        color: '#fff',
        lineHeight: 1.2,
        minWidth: '18px',
        textAlign: 'center',
        marginTop: '2px'
      }}>
        {progress.shortLabel}
      </div>
    </Tooltip>
  );
};

const TableProjectComponent = (props) => {

  const {
    _id,
    id,
    project_id,
    source_file,
    length,
    languages = [],
    name,
    project_archived,
    fileDate,
    ref_id,
    file_video_title,
    proofread_assignee,
    proofread_status,
    ref_proofread_assignee,
    proofread_date_start,
    subtitles_assignee,
    subtitles_status,
    subtitles_date_start,
    subtitles_check_assignee,
    subtitles_check_start_date,
    translate_assignee,
    translate_start_date,
    translate_check_assignee,
    translate_check_start_date,

    pageMode,

    parts,
    parts_count,
    clip_count,
    skip_last_parts,
    available_languages,
    language_progress,  // Прогресс языков из API (для облегчённого режима)
    language_task_status,  // Статус задач языков из API (для облегчённого режима)
    detailsLoaded,
    langsShown,
    onDataRefresh,  // callback для обновления данных после bulk assign
    onOpenClips,    // callback (fid) для открытия дровера с клипами из списка
    partsDuration   // длительность каждой части из files_to_send
  } = { ...props };

  const context = useContext(Context);

  const [expanded, setExpanded] = useState(pageMode ? true : false)
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)

  // Загружаем детали проекта при раскрытии
  useEffect(() => {
    if (expanded && !detailsLoaded && !loadingDetails && !pageMode) {
      setLoadingDetails(true);
      context.loadProjectDetails(id).finally(() => {
        setLoadingDetails(false);
      });
    }
  }, [expanded, detailsLoaded, id, pageMode]);

  const getFilteredLanguages = () => {
    // Если детали не загружены, используем available_languages из облегченного ответа
    if (!detailsLoaded && available_languages && available_languages.length > 0) {
      if (langsShown === 'all') {
        return available_languages.filter(Boolean);
      }
      const nl = normalizeLangId(langsShown);
      return available_languages.includes(nl) ? [nl] : [];
    }

    // Если детали загружены, используем полные данные parts
    if (langsShown === 'all') {
      // Get all unique languages from all parts
      const allLanguages = new Set();
      parts?.forEach(part => {
        part.languages?.forEach(lang => {
          allLanguages.add(lang.id);
        });
      });
      return Array.from(allLanguages);
    }

    // Now langsShown is the actual language code (e.g., 'eng', 'es', 'kz') — normalize backend
    // aliases (en->eng, ru->rus, ch->zh) so the flag/column resolves and never white-screens.
    return [normalizeLangId(langsShown)].filter(Boolean);
  };

  return (

    <>
      <div className={expanded ? "t_project0 expanded" : "t_project0"}>



        <div className="t_project_header"
          style={{
            top: '4em',
            position: pageMode ? 'static' : 'sticky',
          }}
          onClick={() => setExpanded(prev => !prev)}
        >

          <div className="t_project">
            <div className="t_project_info">
              {!pageMode && (
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', marginRight: '6px' }}>
                  <Checkbox
                    checked={context?.state?.selectedIds?.has(id) || false}
                    onChange={() => context.toggleSelect(id)}
                  />
                </div>
              )}
              {!pageMode && <div className="t_project_expand">
                <div className="t_icon">
                  {!expanded && <ExpandMoreIcon />}
                  {expanded && <ExpandLessIcon />}
                </div>
              </div>}
              {!pageMode && <div className="t_project_name " style={{ maxWidth: '50em' }}>
                {name}
                <span style={{ color: '#888', fontSize: '0.85em', marginLeft: '8px' }}>[{id}]</span>
              </div>}

              {project_archived && <Tag color="green">Выполнен</Tag>}
              {/* <div className="_f _fgap04">{parts.length > 0 && parts[0].languages.map(lang => getLanguageFlag(lang.id))}</div> */}


            </div>


            <div className="t_project_actions _fm _fgap04">


              {getFilteredLanguages().length > 0 && <div className="_f _fgap02">
                {getAllLanguages(langsShown === 'all' ? null : langsShown).map(langId => {
                  const hasLanguage = getFilteredLanguages().includes(langId);
                  // Используем прогресс из API (если детали не загружены) или пересчитываем на клиенте
                  const progress = hasLanguage
                    ? (detailsLoaded
                      ? getLanguageProgressForProject(parts, langId, skip_last_parts || 0)
                      : language_progress?.[langId])
                    : null;

                  // Вычисляем статус задач для цвета фона флага
                  const taskStatus = hasLanguage
                    ? (detailsLoaded
                      ? getLanguageTaskStatus(parts, langId, skip_last_parts || 0)
                      : language_task_status?.[langId] || 'none')
                    : 'none';

                  // Определяем цвет фона флага в зависимости от статуса задач
                  const getFlagBackgroundColor = () => {
                    if (!hasLanguage) return 'transparent';
                    switch (taskStatus) {
                      case 'done': return 'rgba(82, 196, 26, 0.3)'; // зелёный
                      case 'in_progress': return 'rgba(24, 144, 255, 0.3)'; // синий
                      default: return 'transparent'; // белый (без задач)
                    }
                  };

                  return (
                    <div key={langId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Tag color={hasLanguage ? 'white' : 'red'} style={{
                        marginBottom: 0,
                        backgroundColor: getFlagBackgroundColor()
                      }}>
                        <div style={{ marginTop: '-0.2em' }}>
                          <Flag langId={langId} height={8} />
                        </div>
                      </Tag>
                      {hasLanguage && progress && <LanguageProgressBadge progress={progress} />}
                    </div>
                  )
                })}
              </div>}


              {project_archived && <Tag color="green">Выполнен</Tag>}
              {((detailsLoaded && parts.length === 0) || (!detailsLoaded && parts_count === 0)) && <Tag color="red">Проект пуст</Tag>}
              {!detailsLoaded && parts_count > 0 && <Tag color="blue">{parts_count} {parts_count === 1 ? 'часть' : 'частей'}</Tag>}
              {onOpenClips && clip_count > 0 && (
                <Tag
                  color="geekblue"
                  style={{ cursor: 'pointer' }}
                  title="Показать клипы этой лекции"
                  onClick={(e) => { e.stopPropagation(); onOpenClips(id); }}
                >
                  🎬 {clip_count}
                </Tag>
              )}

              {!pageMode && <Button size="small" onClick={() => {
                window.open(`/project/${id}`, '_self')
              }}>
                <LaunchIcon style={{ fontSize: '1.2em' }} />
              </Button>}

              {/* Кнопка временно скрыта - можно вернуть, убрав false && */}
              {false && <Popover
                placement="bottomLeft"
                content={<ProjectMaterials {...props} />}
                // title="Материалы проекта"
                trigger="click"
              >
                <Button size="small" onClick={(e) => {
                  e.stopPropagation()
                  setIsInfoOpen(prev => !prev)
                }}>
                  <InfoCircleOutlined />
                </Button>
              </Popover>}
            </div>
          </div>


          {/* {!expanded
            && !project_archived
            && <TableProjectsInfo {...props} langsShown={langsShown} />} */}

        </div>



        <div className={expanded ? "t_expand expanded" : "t_expand"}>

          <div className="t0">

            {parts.length === 0 && <div className="t_cols t_cols--th"
              style={{ margin: '0 auto', width: '100%' }}
            >
              <div className="_f _fcc _fv _f100" style={{ height: '6em' }}>
                <div>Тут пусто</div>
                <Button type="primary" size="small" onClick={() => {
                  console.log('parts = ', parts);
                }}>
                  <div>Добавить файлы</div>
                </Button>
              </div>
            </div>}


            <div className="t_cols" style={{ margin: '0 auto', width: '100%' }}>

              {/* <div className="t_c t_c--step1 _fv _f100" style={{ width: '30em' }}>
                <div className="t_col">
                  <div className="t_line">

                    {source_file}

                  </div>
                </div>
              </div> */}

              <div className="t_c_parts0" style={{ width: '100%', overflowY: "auto" }}>

                {loadingDetails && (
                  <div style={{ padding: '2em', textAlign: 'center' }}>
                    <Spin size="large" tip="Загрузка деталей проекта..." />
                  </div>
                )}

                {/* Строка заголовков с той же структурой что и Part */}
                {!loadingDetails && parts.length > 0 && (
                  <div className="t_c_part t_c_part--header" style={{ width: '100%', borderBottom: '2px solid #e8e8e8', marginBottom: '8px', boxShadow: 'none' }}>
                    <div className="t_c t_c--num" style={{ width: '6em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 600 }}>#</span>
                    </div>
                    <div className="t_c t_c--step0" style={{ width: '16em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', paddingLeft: '1em' }}>
                      <span style={{ fontWeight: 600 }}>Дословный</span>
                    </div>
                    <div className="t_c_langs0">
                      <div className="t_c_lang" style={{ minHeight: 'auto' }}>
                        <div className="t_c t_c--step_lang" style={{ width: '4em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontWeight: 600 }}>Язык</span>
                        </div>
                        <div className="t_c t_c--step_translate" style={{ width: '28em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <BulkActionsPopover
                            projectId={id}
                            partsCount={parts.length}
                            categoryGroups={[
                              { name: 'Перевод', types: ['auto_translation', 'translation_proofreading'] },
                              { name: 'Озвучка', types: ['voicing', 'voicing_proofreading'] }
                            ]}
                            defaultCategory="Перевод"
                            columnName="Перевод"
                            onAssigned={onDataRefresh}
                            onUpdated={onDataRefresh}
                          />
                        </div>
                        <div className="t_c t_c--voicing" style={{ width: '28em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <BulkActionsPopover
                            projectId={id}
                            partsCount={parts.length}
                            categoryGroups={[
                              { name: 'Перевод', types: ['auto_translation', 'translation_proofreading'] },
                              { name: 'Озвучка', types: ['voicing', 'voicing_proofreading'] }
                            ]}
                            defaultCategory="Озвучка"
                            columnName="Озвучка"
                            onAssigned={onDataRefresh}
                            onUpdated={onDataRefresh}
                          />
                        </div>
                        <div className="t_c t_c--full_video" style={{ width: '14em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <BulkActionsPopover
                            projectId={id}
                            partsCount={parts.length}
                            taskTypes={['full_video', 'full_video_proofreading']}
                            columnName="Полное видео"
                            onAssigned={onDataRefresh}
                            onUpdated={onDataRefresh}
                            simplified
                          />
                        </div>
                        <div className="t_c t_c--ts_playlist" style={{ width: '14em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <BulkActionsPopover
                            projectId={id}
                            partsCount={parts.length}
                            taskTypes={['ts_playlist']}
                            columnName="Плейлисты"
                            onAssigned={onDataRefresh}
                            onUpdated={onDataRefresh}
                          />
                        </div>
                        <div className="t_c t_c--loading_in_s3" style={{ width: '14em', flexShrink: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          <BulkActionsPopover
                            projectId={id}
                            partsCount={parts.length}
                            taskTypes={['loading_in_s3']}
                            columnName="Загрузка в S3"
                            onAssigned={onDataRefresh}
                            onUpdated={onDataRefresh}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!loadingDetails && (() => {
                  const sortedParts = [...parts].sort((a, b) => parseInt(a.id) - parseInt(b.id));
                  const skipCount = skip_last_parts || 0;
                  const skippedIds = new Set(
                    skipCount > 0 && sortedParts.length > skipCount
                      ? sortedParts.slice(sortedParts.length - skipCount).map(p => p.id)
                      : []
                  );
                  return sortedParts.map((part) => (
                    <div key={part.id} id={`part-${part.id}`}>
                      <Part
                        {...part}
                        langsShown={langsShown}
                        project_id={id}
                        partDuration={partsDuration?.[String(part.id)]}
                        isSkipped={skippedIds.has(part.id)}
                      />
                    </div>
                  ));
                })()}

              </div>
            </div>
          </div>

        </div>
      </div >
    </>
  )
}

// Memoize component to prevent unnecessary re-renders
// Component will only re-render if props change
export const TableProject = React.memo(TableProjectComponent);
